import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FileText, Shield, CheckCircle2, ArrowRight, Zap, Lock, Download, Mail, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { db } from '@/src/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { trackEvent } from '@/src/lib/analytics';
import { handleFirestoreError, OperationType } from '@/src/lib/firestore-errors';

export default function RiskReportLeadMagnet() {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const leadRef = await addDoc(collection(db, 'leads'), {
        name,
        email,
        type: 'lead_magnet_risk_report',
        status: 'new',
        createdAt: serverTimestamp(),
        source: 'risk_report_landing'
      });

      // Add automated email sequence for lead magnet
      const emailSequence = [
        { 
          type: 'immediate', 
          subject: `Your Free Cybersecurity Risk Report is Here - Next Steps for ${name}`, 
          content: `Hello ${name},\n\nThank you for requesting the 2026 Cyber Risk Report. You can download your copy immediately here: [Link to PDF].\n\nNext Steps:\n1. Review the "AI-Powered Phishing" section on page 12.\n2. Check the "Ransomware 3.0" checklist on page 28.\n3. If you'd like a custom walkthrough of these risks for your business, book a discovery call here: [Link].\n\nStay secure,\nThe AGRANDINTL Team`,
          scheduledAt: new Date().toISOString(),
          status: 'sent',
          sentAt: new Date().toISOString()
        },
        { 
          type: '24h', 
          subject: 'The Hidden Cost of Inaction: Why Cybersecurity is a Business Priority', 
          content: `Hello ${name},\n\nI hope you had a chance to start reviewing the Risk Report. One thing many leaders miss is that cybersecurity is no longer just an "IT issue"—it's an existential business risk.\n\nWith the average cost of a data breach reaching $4.45M, the price of inaction is far higher than the cost of defense. Proactive hardening protects not just your data, but your revenue and brand reputation.\n\nBest regards,\nMarcus Vane\nChief Intelligence Officer, AGRANDINTL`,
          scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        },
        { 
          type: '3d', 
          subject: 'Case Study: How We Prevented a $2M Ransomware Attack', 
          content: `Hello ${name},\n\nIn the report, we mentioned the rise of sophisticated ransomware. We recently worked with a global retail group that had a critical, "unfindable" vulnerability in their cloud infrastructure.\n\nBy identifying and patching this single point of failure before hackers could, we saved them an estimated $2M in potential ransomware costs and weeks of downtime.\n\nYou can read the full breakdown here: [Link to Case Study].\n\nStay vigilant,\nThe AGRANDINTL Team`,
          scheduledAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        },
        { 
          type: '5d', 
          subject: 'Ready to Harden Your Defenses? Let\'s Talk Strategy.', 
          content: `Hello ${name},\n\nYou've read the report and seen the results we deliver for our clients. Now, it's time to build your own custom defense roadmap.\n\nI'd like to offer you a confidential 15-minute strategy session to discuss your specific architecture and how we can eliminate your highest-priority risks.\n\nBook your session here: [Link to Calendar].\n\nTo your resilience,\nMarcus Vane\nAGRANDINTL`,
          scheduledAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        }
      ];

      for (const emailItem of emailSequence) {
        await addDoc(collection(db, `leads/${leadRef.id}/emails`), emailItem);
      }

      trackEvent('lead_magnet_download', { magnet_name: 'risk_report' });
      setIsSuccess(true);
      
      // Simulate download
      setTimeout(() => {
        window.open('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', '_blank');
      }, 1000);

    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative pt-32 pb-24 overflow-hidden min-h-screen">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Side: Offer Copy */}
            <div className="space-y-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
              >
                <FileText className="w-3 h-3" /> Exclusive Intelligence
              </motion.div>

              <div className="space-y-6">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl md:text-8xl font-display font-bold tracking-tighter leading-[0.8]"
                >
                  2026 Cyber <br />
                  <span className="text-accent glow-text">Risk Report</span>
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
                >
                  Download our definitive guide to the evolving threat landscape. Learn how elite organizations are defending against AI-driven attacks and ransomware.
                </motion.p>
              </div>

              <div className="space-y-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">What's Inside:</h3>
                <div className="grid gap-4">
                  {[
                    "The Rise of AI-Powered Phishing: How to spot the invisible",
                    "Ransomware 3.0: Why traditional backups are no longer enough",
                    "Cloud Misconfiguration: The #1 entry point for data breaches",
                    "A 10-Step Checklist for Total Infrastructure Hardening"
                  ].map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-foreground/80">{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Button 
                  onClick={() => setShowModal(true)}
                  size="lg" 
                  className="h-16 px-12 rounded-full bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_30px_rgba(0,150,255,0.3)] group"
                >
                  Download Free Report <Download className="ml-2 w-5 h-5 transition-transform group-hover:translate-y-1" />
                </Button>
                <p className="mt-4 text-xs text-muted-foreground font-light italic">
                  Join 10,000+ security professionals who have already downloaded this report.
                </p>
              </motion.div>
            </div>

            {/* Right Side: Visual Mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative"
            >
              <div className="absolute -inset-10 bg-accent/20 blur-[100px] rounded-full opacity-30" />
              <div className="relative glass-card aspect-[3/4] rounded-[3rem] border-white/10 overflow-hidden shadow-2xl group">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent opacity-50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center space-y-8">
                  <div className="w-32 h-32 rounded-3xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent group-hover:scale-110 transition-transform duration-700">
                    <Shield className="w-16 h-16" />
                  </div>
                  <div className="space-y-4">
                    <h2 className="text-4xl font-display font-bold tracking-tight">CYBERSECURITY <br /> RISK REPORT</h2>
                    <div className="h-1 w-24 bg-accent mx-auto" />
                    <p className="text-xs font-mono tracking-[0.3em] uppercase text-muted-foreground">AGRANDINTL INTELLIGENCE UNIT</p>
                  </div>
                  <div className="pt-12 grid grid-cols-2 gap-8 w-full">
                    <div className="text-left space-y-1">
                      <p className="text-[10px] font-bold text-accent uppercase tracking-widest">Version</p>
                      <p className="text-lg font-bold">2026.1</p>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-[10px] font-bold text-accent uppercase tracking-widest">Pages</p>
                      <p className="text-lg font-bold">42</p>
                    </div>
                  </div>
                </div>
                {/* Decorative lines */}
                <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-background to-transparent" />
                <div className="absolute top-10 left-10 w-20 h-20 border-t-2 border-l-2 border-accent/30 rounded-tl-3xl" />
                <div className="absolute bottom-10 right-10 w-20 h-20 border-b-2 border-r-2 border-accent/30 rounded-br-3xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Email Capture Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl overflow-hidden"
            >
              <button 
                onClick={() => setShowModal(false)}
                className="absolute top-6 right-6 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {!isSuccess ? (
                <div className="space-y-8">
                  <div className="text-center space-y-2">
                    <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center text-accent mx-auto mb-6">
                      <Mail className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-display font-bold">Where should we send it?</h2>
                    <p className="text-sm text-muted-foreground font-light">Enter your details to receive the 2026 Risk Report instantly.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-4">Full Name</label>
                      <Input 
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Doe"
                        className="h-14 rounded-2xl bg-white/5 border-white/10 focus:border-accent/50 focus:ring-accent/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-4">Work Email</label>
                      <Input 
                        required
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="john@company.com"
                        className="h-14 rounded-2xl bg-white/5 border-white/10 focus:border-accent/50 focus:ring-accent/20 transition-all"
                      />
                    </div>
                    <Button 
                      disabled={isSubmitting}
                      className="w-full h-14 rounded-2xl bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_20px_rgba(0,150,255,0.2)]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...
                        </>
                      ) : (
                        <>
                          Send My Report <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                  <p className="text-[10px] text-center text-muted-foreground font-light">
                    By clicking, you agree to receive security intelligence updates. Unsubscribe anytime.
                  </p>
                  <div className="pt-6 border-t border-white/5 flex items-center justify-center gap-4 text-[9px] text-muted-foreground uppercase tracking-widest font-bold">
                    <span className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-accent" /> SSL Secured</span>
                    <span className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-accent" /> Data Protected</span>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-8 py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-500 mx-auto"
                  >
                    <CheckCircle2 className="w-10 h-10" />
                  </motion.div>
                  <div className="space-y-2">
                    <h2 className="text-3xl font-display font-bold">Check Your Inbox!</h2>
                    <p className="text-sm text-muted-foreground font-light leading-relaxed">
                      We've sent the report to <strong>{email}</strong>. Your download should also start automatically in a moment.
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowModal(false)}
                    variant="outline" 
                    className="h-12 rounded-xl border-white/10 hover:bg-white/5"
                  >
                    Close Window
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
